import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fmtUSD, fmtPct } from "@/lib/api";
import { Panel, Metric, Money, Pct, StatusBadge, Table, Empty } from "@/components/terminal";
import { Building2, ArrowRight } from "lucide-react";

const RISK_CLS = {
  "NORMAL": "text-emerald-400", "EMERGENCY STOP": "text-red-400",
  "TAX UNDERFUNDED": "text-amber-400", "CRITICAL EVENT": "text-red-400",
};

export default function CommandCenter() {
  const [d, setD] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    api.get("/command-center", { params: { account: undefined } }).then((r) => setD(r.data)).catch(() => {});
  }, []);
  if (!d) return <div className="text-slate-500 font-mono text-sm" data-testid="cc-loading">Loading command center…</div>;
  const t = d.totals;

  const openAccount = (id) => {
    localStorage.setItem("hussar_account", id);
    navigate("/");
    window.location.reload();
  };

  const familyCards = d.accounts.filter((c) => c.account.account_type !== "BUSINESS");
  const corpCards = d.accounts.filter((c) => c.account.account_type === "BUSINESS");

  const renderCard = (c) => (
    <button key={c.account.id} onClick={() => openAccount(c.account.id)} data-testid={`account-card-${c.account.id}`}
      className="panel p-5 text-left hover:border-[#D4AF37]/40 transition-colors group">
      <div className="flex items-center justify-between mb-3">
        <div>
          <span className="font-display font-bold tracking-wide text-slate-100 uppercase">{c.account.name}</span>
          <span className="ml-2 text-[10px] text-slate-500 uppercase tracking-wider">{c.account.account_type}{c.account.beneficiary_status === "MINOR" ? " · minor" : ""}</span>
          {c.account.account_type === "BUSINESS" && (
            <span data-testid={`account-business-badge-${c.account.id}`}
              className="ml-2 text-[9px] font-bold tracking-widest text-slate-300 border border-slate-500/60 bg-slate-800/40 rounded-sm px-1.5 py-0.5 align-middle">CORPORATE</span>
          )}
          {c.initialized && (
            <span data-testid={`account-initialized-badge-${c.account.id}`}
              className="ml-2 text-[9px] font-bold tracking-widest text-[#0B0C0E] bg-[#D4AF37] rounded-sm px-1.5 py-0.5 align-middle">INITIALIZED</span>
          )}
        </div>
        <ArrowRight className="w-4 h-4 text-slate-600 group-hover:text-[#D4AF37] transition-colors" />
      </div>
      {c.onboarding && (
        <div data-testid={`corporate-onboarding-chip-${c.account.id}`}
          className={`mb-3 px-3 py-1.5 rounded-sm border text-[10px] font-bold tracking-widest ${
            c.onboarding.chip.state === "ELIGIBLE" ? "border-emerald-700 bg-emerald-950/30 text-emerald-400"
            : c.onboarding.chip.state === "LAPSED" ? "border-amber-700 bg-amber-950/30 text-amber-300"
            : "border-[#3F4656] bg-[#121316] text-slate-400"}`}>
          {c.onboarding.chip.text}
        </div>
      )}
      <div className="font-mono text-2xl font-bold tabular-nums text-slate-100 mb-3">{fmtUSD(c.nav)}</div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] text-slate-500">
        <span>Return <span className="float-right"><Pct value={c.return_pct} /></span></span>
        <span>Cash <span className="font-mono text-slate-300 float-right">{fmtUSD(c.cash, 0)}</span></span>
        <span>BTC <span className="font-mono text-slate-300 float-right">{fmtUSD(c.btc_value, 0)}</span></span>
        <span>Core Digital <span className="font-mono text-slate-300 float-right">{fmtUSD(c.crypto_value, 0)}</span></span>
        <span>Opportunity Reserve <span className="font-mono text-slate-300 float-right">{fmtUSD(c.opportunity_reserve, 0)}</span></span>
        <span>Wildcard <span className="font-mono text-slate-300 float-right">{fmtUSD(c.wildcard_nav, 0)}</span></span>
        <span>Tax Reserve <span className="font-mono text-slate-300 float-right">{fmtUSD(c.tax_reserve, 0)}</span></span>
      </div>
      <div className="flex items-center justify-between mt-3 text-[10px] text-slate-500">
        <span data-testid={`account-provider-${c.account.id}`}>Provider: {(c.provider_status || "DISCONNECTED").replace(/_/g, " ")}</span>
        <span data-testid={`account-recon-${c.account.id}`}
          className={c.reconciliation_status === "PASS" ? "text-emerald-400" : c.reconciliation_status === "FAIL" ? "text-red-400" : "text-amber-300"}>
          Reconciliation: {c.reconciliation_status || "NOT RUN"}
        </span>
      </div>
      {c.custody && c.custody.wallets > 0 && (
        <div data-testid={`account-custody-${c.account.id}`}
          className={`mt-2 px-3 py-1.5 rounded-sm border text-[10px] font-mono tracking-wider ${
            c.custody.reconciliation === "PASS" ? "border-emerald-700 bg-emerald-950/30 text-emerald-400"
            : c.custody.reconciliation === "FAIL" ? "border-red-700 bg-red-950/30 text-red-400"
            : "border-amber-700 bg-amber-950/30 text-amber-300"}`}>
          DIGITAL ASSET CUSTODY · WALLETS {c.custody.wallets} · ASSETS {c.custody.assets_held} · PROVENANCE {c.custody.provenance_complete} · {c.custody.reconciliation}
        </div>
      )}
      <div className="flex items-center justify-between mt-2 pt-3 border-t border-[#222630]">
        <span className={`text-[11px] font-bold tracking-wide ${RISK_CLS[c.risk_status] || "text-slate-400"}`} data-testid={`account-risk-${c.account.id}`}>Risk: {c.risk_status}</span>
        <StatusBadge status={c.execution_mode} />
      </div>
    </button>
  );

  return (
    <div className="space-y-5" data-testid="command-center-page">
      <div className="flex items-center gap-3">
        <Building2 className="w-5 h-5 gold-text" />
        <h2 className="font-display text-xl font-bold">HUSSAR TREASURY COMMAND CENTER</h2>
      </div>
      <p className="text-xs text-slate-500 max-w-3xl">{d.note}</p>

      {d.regime && (
        <div className={`panel px-4 py-2.5 flex flex-wrap items-center gap-x-4 gap-y-1 ${(d.regime.stale || d.regime.state === "UNCERTAIN") ? "border-amber-700" : ""}`}
          data-testid="regime-timeline-strip">
          <span className="text-[10px] font-mono text-slate-500 tracking-widest">CURRENT REGIME</span>
          <span className={`text-xs font-mono font-bold ${d.regime.state === "UNCERTAIN" ? "text-amber-300" : "text-slate-100"}`}
            data-testid="regime-timeline-state">{d.regime.state.replace(/_/g, " ")}</span>
          <span className="text-[10px] font-mono text-slate-500" data-testid="regime-timeline-confidence">CONFIDENCE {d.regime.confidence}%</span>
          <span className={`text-[10px] font-mono ${d.regime.stale ? "text-amber-300 font-bold" : "text-slate-500"}`}
            data-testid="regime-timeline-age">AGE {d.regime.age_minutes}M{d.regime.stale ? " · STALE" : ""}</span>
          <span className="text-[10px] font-mono text-slate-500" data-testid="regime-timeline-last-transition">
            LAST TRANSITION {d.regime.last_transition
              ? `${d.regime.last_transition.from_state.replace(/_/g, " ")} → ${d.regime.last_transition.to_state.replace(/_/g, " ")}`
              : "—"}
          </span>
        </div>
      )}

      <div className="panel-elevated p-5 border-l-2 border-l-[#D4AF37]" data-testid="cc-hero">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
          <div>
            <div className="metric-label">Seed Capital</div>
            <div className="font-mono text-2xl font-bold tabular-nums text-slate-100" data-testid="cc-seed">{fmtUSD(t.total_seed_capital)}</div>
          </div>
          <div>
            <div className="metric-label">Current NAV</div>
            <div className="font-mono text-2xl font-bold tabular-nums text-slate-100" data-testid="cc-nav">{fmtUSD(t.combined_nav)}</div>
          </div>
          <div>
            <div className="metric-label">Value Created</div>
            <div className="font-mono text-2xl font-bold tabular-nums" data-testid="cc-value-created">
              <Money value={t.value_created_above_seed} tone={t.value_created_above_seed >= 0 ? "pos" : "neg"} />
            </div>
          </div>
          <div>
            <div className="metric-label">Net Platform NAV After Est. Tax</div>
            <div className="font-mono text-2xl font-bold tabular-nums gold-text" data-testid="cc-net-nav">{fmtUSD(t.combined_net_nav)}</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="cc-treasury-split">
        <div className="panel p-4" data-testid="cc-family-treasury">
          <div className="metric-label">Family Treasury NAV</div>
          <div className="font-mono text-xl font-bold tabular-nums text-slate-100 mt-1">{fmtUSD(t.family_nav)}</div>
          <div className="text-[10px] text-slate-500 mt-1">Family Accounts: {t.family_accounts}</div>
        </div>
        <div className="panel p-4" data-testid="cc-corporate-treasury">
          <div className="metric-label">Corporate Treasury NAV</div>
          <div className="font-mono text-xl font-bold tabular-nums text-slate-100 mt-1">{fmtUSD(t.corporate_nav)}</div>
          <div className="text-[10px] text-slate-500 mt-1">Business Accounts: {t.business_accounts}</div>
        </div>
        <div className="panel p-4 border-l-2 border-l-[#D4AF37]" data-testid="cc-platform-treasury">
          <div className="metric-label">Total Platform NAV (reporting only)</div>
          <div className="font-mono text-xl font-bold tabular-nums gold-text mt-1">{fmtUSD(t.combined_nav)}</div>
          <div className="text-[10px] text-slate-500 mt-1">Total Active Accounts: {t.total_accounts}</div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-3">
        <Metric label="Total Active Accounts" value={String(t.total_accounts)} testId="cc-accounts" />
        <Metric label="Today's Change" value={fmtUSD(t.combined_today_change)} tone={t.combined_today_change >= 0 ? "pos" : "neg"} testId="cc-today" />
        <Metric label="All-Time Return" value={fmtPct(t.combined_all_time_return_pct)} tone={t.combined_all_time_return_pct >= 0 ? "pos" : "neg"} testId="cc-return" />
        <Metric label="Realized P/L" value={fmtUSD(t.combined_realized_pl)} tone={t.combined_realized_pl >= 0 ? "pos" : "neg"} testId="cc-realized" />
        <Metric label="Unrealized P/L" value={fmtUSD(t.combined_unrealized_pl)} tone={t.combined_unrealized_pl >= 0 ? "pos" : "neg"} testId="cc-unrealized" />
        <Metric label="Combined Cash" value={fmtUSD(t.combined_cash)} testId="cc-cash" />
        <Metric label="Core Assets" value={fmtUSD(t.combined_core_assets)} testId="cc-core" />
        <Metric label="Opportunity Reserve" value={fmtUSD(t.combined_opportunity_reserve)} testId="cc-opp" />
        <Metric label="Wildcard Value" value={fmtUSD(t.combined_wildcard_value)} testId="cc-wildcard" />
        <Metric label="Wildcard P/L" value={fmtUSD(t.combined_wildcard_pl)} tone={t.combined_wildcard_pl >= 0 ? "pos" : "neg"} testId="cc-wildcard-pl" />
        <Metric label="Tax Reserve" value={fmtUSD(t.combined_tax_reserve)} testId="cc-tax-reserve" />
        <Metric label="Core Created From Wildcard" value={fmtUSD(t.core_capital_created_from_wildcard)} tone="gold" testId="cc-core-from-wildcard" />
      </div>

      <div data-testid="cc-family-section">
        <div className="metric-label mb-2">FAMILY TREASURY — {familyCards.length} ACCOUNTS · PERSONAL / CUSTODIAL</div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {familyCards.map(renderCard)}
        </div>
      </div>

      <div data-testid="cc-corporate-section">
        <div className="metric-label mb-2">CORPORATE TREASURY — {corpCards.length} ACCOUNT · HUSSAR HOLDINGS LLC · NEVER POOLED WITH FAMILY FUNDS</div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {corpCards.map(renderCard)}
        </div>
      </div>

      <Panel title="Account Benchmarking (comparison, not competition)" testId="cc-benchmarks">
        {d.accounts.length === 0 ? <Empty>No accounts visible.</Empty> : (
          <Table testId="cc-benchmark-table" head={[{ label: "Account" }, { label: "Profile" }, { label: "Return %", num: true }, { label: "Volatility %", num: true }, { label: "Max Drawdown %", num: true }, { label: "Income", num: true }, { label: "Wildcard P/L", num: true }, { label: "Tax Efficiency", num: true }]}>
            {d.accounts.map((c) => (
              <tr key={c.account.id} className="hover:bg-[#222630]" data-testid={`bench-row-${c.account.id}`}>
                <td className="td-cell font-semibold text-slate-100">{c.account.name}</td>
                <td className="td-cell text-slate-500">{c.account.risk_profile}</td>
                <td className="td-num"><Pct value={c.benchmark.return_pct} /></td>
                <td className="td-num">{c.benchmark.volatility_pct}%</td>
                <td className="td-num">{c.benchmark.max_drawdown_pct}%</td>
                <td className="td-num">{fmtUSD(c.benchmark.income_generated)}</td>
                <td className="td-num"><Money value={c.benchmark.wildcard_pl} tone={c.benchmark.wildcard_pl >= 0 ? "pos" : "neg"} /></td>
                <td className="td-num">{c.benchmark.tax_efficiency_pct != null ? `${c.benchmark.tax_efficiency_pct}%` : "—"}</td>
              </tr>
            ))}
          </Table>
        )}
      </Panel>
    </div>
  );
}
